import { Alert, Button, LinkButton, PageHeader, problemText, Section, Select, TextField } from "@emdur/ui";
import { type FormEvent, useEffect, useState } from "react";
import { AllOrganizations } from "../components/AllOrganizations";
import { call, SignInRequired } from "../lib/api";
import { startSignIn } from "../lib/auth";
import { errorText } from "../lib/errors";
import { guard } from "../lib/guard";

type Me = { person: { display_name: string }; memberships: { organization_id: string; organization_name: string; roles: string[] }[] };

const KINDS = [
  { value: "customer", label: "Customer (a business using a product)" },
  { value: "product", label: "Product (an Emdur product)" },
];
const EMPTY = { kind: "customer", display_name: "", legal_name: "", country: "KE" };

export function Home() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState(EMPTY);
  const [created, setCreated] = useState<{ id: string; display_name: string } | null>(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    call("GET", "/v1/me")
      .then(async (r) => (r.ok ? setMe((await r.json()) as Me) : setError(new Error(`could not load (${r.status})`))))
      .catch((e: unknown) => (e instanceof SignInRequired ? startSignIn("/") : setError(e)));
  }, []);

  const run = (work: Promise<unknown>) => guard(work, () => startSignIn("/"), setError);

  function create(event: FormEvent) {
    event.preventDefault();
    void run(
      (async () => {
        const r = await call("POST", "/v1/organizations", form);
        if (!r.ok) throw new Error(await problemText(r));
        // The creator administers the platform, not the new organisation: its owner joins by invitation (W1.2).
        setCreated((await r.json()) as { id: string; display_name: string });
        setSent(null);
        setRefresh((n) => n + 1); // it is listed under All organisations, so it stays reachable after a reload
        // A fresh form for the next organisation: a kept "product" once made a customer a product.
        setForm(EMPTY);
      })(),
    );
  }

  function inviteOwner(event: FormEvent) {
    event.preventDefault();
    void run(
      (async () => {
        const r = await call("POST", `/v1/organizations/${created!.id}/invitations`, { email: ownerEmail, roles: ["admin"] });
        if (!r.ok) throw new Error(await problemText(r));
        setSent(`Invitation sent to ${ownerEmail}. ${created!.display_name} becomes active when they accept.`);
      })(),
    );
  }

  if (!me) return error ? <Alert tone="danger">{errorText(error)}</Alert> : null;
  return (
    <>
      <PageHeader title={`Hello, ${me.person.display_name}`} />
      <div className="flex flex-col gap-8">
        <Section title="Your organisations">
          <ul aria-label="Your organisations" className="flex flex-col gap-2">
            {me.memberships.map((m) => (
              <li key={m.organization_id} className="flex flex-wrap items-center gap-2">
                <LinkButton variant="quiet" href={`/orgs/${m.organization_id}`}>
                  {m.organization_name}
                </LinkButton>
                <span className="text-sm text-muted">({m.roles.join(", ")})</span>
              </li>
            ))}
          </ul>
        </Section>
        <AllOrganizations run={run} refresh={refresh} />
        {created && (
          <Section title={`Invite the owner of ${created.display_name}`}>
            <form onSubmit={inviteOwner} className="flex flex-col gap-3">
              <TextField label="Owner's email" type="email" value={ownerEmail} onChange={setOwnerEmail} isRequired />
              <div>
                <Button type="submit" variant="primary">
                  Send invitation
                </Button>
              </div>
              {sent && <Alert tone="positive">{sent}</Alert>}
            </form>
          </Section>
        )}
        <Section title="Create organisation">
          <form onSubmit={create} className="flex flex-col gap-3">
            <Select label="Kind" options={KINDS} value={form.kind} onChange={(kind) => setForm({ ...form, kind })} />
            <TextField label="Display name" value={form.display_name} onChange={(display_name) => setForm({ ...form, display_name })} isRequired />
            <TextField label="Legal name" value={form.legal_name} onChange={(legal_name) => setForm({ ...form, legal_name })} isRequired />
            <TextField
              label="Country (ISO 3166-1)"
              value={form.country}
              maxLength={2}
              onChange={(country) => setForm({ ...form, country: country.toUpperCase() })}
              isRequired
            />
            <div>
              <Button type="submit" variant="primary">
                Create
              </Button>
            </div>
          </form>
        </Section>
        {error !== null && <Alert tone="danger">{errorText(error)}</Alert>}
      </div>
    </>
  );
}
